import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { WhatsAppButton } from '@/components/WhatsAppButton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Wrench, ArrowRight, CheckCircle, Copy, MessageCircle, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ServicePage = () => {
  // Dropdown data
  const [productTypes, setProductTypes] = useState([]);
  const [allBrands, setAllBrands] = useState([]);
  const [allModels, setAllModels] = useState([]);
  const [allIssues, setAllIssues] = useState([]);
  const [cities, setCities] = useState([]);
  const [partQualities, setPartQualities] = useState([]);
  
  // Filtered options based on selection
  const [filteredBrands, setFilteredBrands] = useState([]);
  const [filteredModels, setFilteredModels] = useState([]);
  const [filteredIssues, setFilteredIssues] = useState([]);
  
  // Selected values
  const [selectedProductType, setSelectedProductType] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedIssue, setSelectedIssue] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedQuality, setSelectedQuality] = useState('');
  
  // Price data
  const [currentPrice, setCurrentPrice] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(false);
  
  const [showForm, setShowForm] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [serviceCode, setServiceCode] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    cargo_number: '',
    appointment_date: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadDropdowns();
  }, []);

  // Fetch price when all selections are made
  useEffect(() => {
    const fetchPrice = async () => {
      if (selectedModel && selectedIssue) {
        setLoadingPrice(true);
        try {
          const modelLabel = filteredModels.find(m => m.value === selectedModel)?.label || selectedModel;
          const brandLabel = filteredBrands.find(b => b.value === selectedBrand)?.label || selectedBrand;
          const issueLabel = filteredIssues.find(i => i.value === selectedIssue)?.label || selectedIssue;
          
          const response = await fetch(`${API_URL}/api/price?model=${encodeURIComponent(modelLabel)}&brand=${encodeURIComponent(brandLabel)}&issue=${encodeURIComponent(issueLabel)}`);
          if (response.ok) {
            const data = await response.json();
            if (data && (data.aftermarket_price || data.original_price)) {
              setCurrentPrice(data);
            } else {
              setCurrentPrice(null);
            }
          } else {
            setCurrentPrice(null);
          }
        } catch (e) {
          setCurrentPrice(null);
        }
        setLoadingPrice(false);
      } else {
        setCurrentPrice(null);
      }
    };
    fetchPrice();
  }, [selectedModel, selectedIssue, selectedBrand, filteredModels, filteredBrands, filteredIssues]);

  // Filter brands when product type changes
  useEffect(() => {
    if (selectedProductType) {
      const filtered = allBrands.filter(b => b.parent === selectedProductType);
      setFilteredBrands(filtered);
      setSelectedBrand('');
      setSelectedModel('');
      setFilteredModels([]);
      setSelectedIssue('');
      setFilteredIssues([]);
    } else {
      setFilteredBrands([]);
      setFilteredIssues([]);
    }
  }, [selectedProductType, allBrands]);

  // Filter models when brand changes
  useEffect(() => {
    if (selectedBrand) {
      const filtered = allModels.filter(m => m.parent === selectedBrand);
      setFilteredModels(filtered);
      setSelectedModel('');
      setSelectedIssue('');
    } else {
      setFilteredModels([]);
    }
  }, [selectedBrand, allModels]);

  // Filter issues when brand changes (by product type AND excluded brands)
  useEffect(() => {
    if (selectedProductType) {
      const filteredIss = allIssues.filter(issue => {
        // Check product type filter
        const productTypeMatch = !issue.parent || issue.parent === selectedProductType;
        if (!productTypeMatch) return false;
        
        // Check excluded brands filter (only if brand is selected)
        if (selectedBrand && issue.excluded_brands) {
          const excludedList = issue.excluded_brands.split(',').map(b => b.trim().toLowerCase());
          if (excludedList.includes(selectedBrand.toLowerCase())) {
            return false; // This brand is excluded
          }
        }
        
        return true;
      });
      setFilteredIssues(filteredIss);
      // Reset issue selection when brand changes
      if (selectedBrand) {
        setSelectedIssue('');
      }
    } else {
      setFilteredIssues([]);
    }
  }, [selectedBrand, selectedProductType, allIssues]);

  const loadDropdowns = async () => {
    try {
      const [ptRes, brandRes, modelRes, issRes, cityRes, qualRes] = await Promise.all([
        fetch(`${API_URL}/api/dropdowns/product_types`),
        fetch(`${API_URL}/api/dropdowns/brands`),
        fetch(`${API_URL}/api/dropdowns/models`),
        fetch(`${API_URL}/api/dropdowns/issues`),
        fetch(`${API_URL}/api/dropdowns/cities`),
        fetch(`${API_URL}/api/dropdowns/part_quality`),
      ]);
      if (ptRes.ok) setProductTypes(await ptRes.json());
      if (brandRes.ok) setAllBrands(await brandRes.json());
      if (modelRes.ok) setAllModels(await modelRes.json());
      if (issRes.ok) setAllIssues(await issRes.json());
      if (cityRes.ok) setCities(await cityRes.json());
      if (qualRes.ok) setPartQualities(await qualRes.json());
    } catch (e) { console.error(e); }
  };

  const handleShowForm = () => {
    const issueHasQuality = filteredIssues.find(i => i.value === selectedIssue)?.has_quality_option !== false;
    
    if (!selectedProductType || !selectedBrand || !selectedModel || !selectedIssue) {
      toast.error('Lütfen tüm seçimleri yapın');
      return;
    }
    
    // If issue doesn't have quality option, auto-set it
    if (!issueHasQuality && !selectedQuality) {
      setSelectedQuality('standard');
    } else if (issueHasQuality && !selectedQuality) {
      toast.error('Lütfen parça kalitesini seçin');
      return;
    }
    
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !selectedCity) {
      toast.error('Lütfen tüm zorunlu alanları doldurun');
      return;
    }

    setIsSubmitting(true);
    try {
      const productLabel = productTypes.find(p => p.value === selectedProductType)?.label || selectedProductType;
      const brandLabel = filteredBrands.find(b => b.value === selectedBrand)?.label || selectedBrand;
      const modelLabel = filteredModels.find(m => m.value === selectedModel)?.label || selectedModel;
      const issueLabel = filteredIssues.find(i => i.value === selectedIssue)?.label || selectedIssue;
      const qualityLabel = partQualities.find(q => q.value === selectedQuality)?.label || selectedQuality;

      const response = await fetch(`${API_URL}/api/appointments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          city: selectedCity,
          product_type: productLabel,
          brand: brandLabel,
          device_model: modelLabel,
          issue_type: issueLabel,
          part_quality: qualityLabel,
          cargo_number: formData.cargo_number || null,
          appointment_date: formData.appointment_date || null,
        }),
      });

      const data = await response.json();
      if (data.success) {
        setServiceCode(data.service_code);
        setShowForm(false);
        setShowSuccessModal(true);
        // Reset form
        setFormData({ name: '', phone: '', cargo_number: '', appointment_date: '' });
        setSelectedProductType('');
        setSelectedBrand('');
        setSelectedModel('');
        setSelectedIssue('');
        setSelectedCity('');
        setSelectedQuality('');
      } else {
        toast.error('Kayıt oluşturulamadı');
      }
    } catch (error) {
      toast.error('Bağlantı hatası');
    }
    setIsSubmitting(false);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(serviceCode);
    toast.success('Kod kopyalandı');
  };

  const goToWhatsApp = () => {
    const message = encodeURIComponent(`Merhaba, servis kaydım oluşturuldu. Kayıt kodum: ${serviceCode}`);
    window.open(`https://wa.me/902323646983?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-8 pb-16">
        {/* Hero */}
        <section className="bg-gradient-hero py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
              <Wrench className="w-5 h-5" />
              <span className="font-medium">Servis Talebi</span>
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-3xl sm:text-4xl lg:text-5xl font-bold font-display text-foreground mb-4">
              Servis Kaydı Oluştur
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-lg text-muted-foreground">
              Cihazınız için servis kaydı oluşturun
            </motion.p>
          </div>
        </section>

        {/* Form Section */}
        <section className="py-12 lg:py-16">
          <div className="max-w-2xl mx-auto px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-card rounded-2xl border shadow-card p-6 lg:p-8">
              
              <div className="space-y-6">
                {/* 1. Product Type */}
                <div className="space-y-2">
                  <Label>1. Ürün Türü *</Label>
                  <Select value={selectedProductType} onValueChange={setSelectedProductType}>
                    <SelectTrigger data-testid="product-type-select"><SelectValue placeholder="Ürün türü seçin" /></SelectTrigger>
                    <SelectContent>
                      {productTypes.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* 2. Brand */}
                <div className="space-y-2">
                  <Label>2. Marka *</Label>
                  <Select value={selectedBrand} onValueChange={setSelectedBrand} disabled={!selectedProductType}>
                    <SelectTrigger data-testid="brand-select"><SelectValue placeholder={selectedProductType ? "Marka seçin" : "Önce ürün türü seçin"} /></SelectTrigger>
                    <SelectContent>
                      {filteredBrands.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* 3. Model */}
                <div className="space-y-2">
                  <Label>3. Model *</Label>
                  <Select value={selectedModel} onValueChange={setSelectedModel} disabled={!selectedBrand}>
                    <SelectTrigger data-testid="model-select"><SelectValue placeholder={selectedBrand ? "Model seçin" : "Önce marka seçin"} /></SelectTrigger>
                    <SelectContent>
                      {filteredModels.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* 4. Issue Type */}
                <div className="space-y-2">
                  <Label>4. Arıza Türü *</Label>
                  <Select value={selectedIssue} onValueChange={setSelectedIssue} disabled={!selectedModel}>
                    <SelectTrigger data-testid="issue-select"><SelectValue placeholder={selectedModel ? "Arıza seçin" : "Önce model seçin"} /></SelectTrigger>
                    <SelectContent>
                      {filteredIssues.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* 5. Part Quality - only show if issue has quality option */}
                {selectedIssue && filteredIssues.find(i => i.value === selectedIssue)?.has_quality_option !== false && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }} 
                    animate={{ opacity: 1, height: 'auto' }} 
                    className="space-y-3"
                  >
                    <Label>5. Parça Kalitesi *</Label>
                    <RadioGroup value={selectedQuality} onValueChange={setSelectedQuality} className="grid grid-cols-2 gap-4">
                      {partQualities.map((q) => {
                        const isAftermarket = q.value === 'aftermarket' || q.value === 'yan-sanayi';
                        const isOriginal = q.value === 'original' || q.value === 'cikma-orijinal';
                        const priceToShow = isAftermarket ? currentPrice?.aftermarket_price : isOriginal ? currentPrice?.original_price : null;
                        
                        return (
                          <div key={q.value}>
                            <RadioGroupItem value={q.value} id={q.value} className="peer sr-only" />
                            <Label htmlFor={q.value} className="flex flex-col items-center justify-center rounded-xl border-2 border-muted bg-popover p-4 hover:bg-accent cursor-pointer peer-data-[state=checked]:border-primary">
                              <span className="text-lg font-semibold">{q.label}</span>
                              {priceToShow && (
                                <span className="text-primary font-bold mt-1">
                                  {typeof priceToShow === 'number' ? `${priceToShow}₺` : (priceToShow.match(/^\d+$/) ? `${priceToShow}₺` : priceToShow)}
                                </span>
                              )}
                            </Label>
                          </div>
                        );
                      })}
                    </RadioGroup>
                    {loadingPrice && <p className="text-sm text-muted-foreground text-center">Fiyat yükleniyor...</p>}
                  </motion.div>
                )}

                {/* Auto-select quality for issues without quality option */}
                {selectedIssue && filteredIssues.find(i => i.value === selectedIssue)?.has_quality_option === false && !selectedQuality && (
                  <motion.div 
                    initial={{ opacity: 0 }} 
                    animate={{ opacity: 1 }}
                    onAnimationComplete={() => setSelectedQuality('standard')}
                  />
                )}

                {/* Price Display Summary */}
                {currentPrice && selectedQuality && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }} 
                    animate={{ opacity: 1, y: 0 }} 
                    className="bg-primary/5 rounded-xl p-4 border border-primary/20"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Tahmini Fiyat:</span>
                      <span className="text-2xl font-bold text-primary">
                        {(() => {
                          const isAftermarket = selectedQuality === 'aftermarket' || selectedQuality === 'yan-sanayi';
                          const isOriginal = selectedQuality === 'original' || selectedQuality === 'cikma-orijinal';
                          const price = isAftermarket ? currentPrice.aftermarket_price : isOriginal ? currentPrice.original_price : currentPrice.aftermarket_price;
                          if (!price) return '-';
                          return typeof price === 'number' ? `${price}₺` : (price.match(/^\d+$/) ? `${price}₺` : price);
                        })()}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">* Kesin fiyat cihaz kontrolünden sonra belirlenir</p>
                  </motion.div>
                )}

                <Button 
                  onClick={handleShowForm} 
                  className="w-full" 
                  size="lg" 
                  disabled={!selectedIssue || (filteredIssues.find(i => i.value === selectedIssue)?.has_quality_option !== false && !selectedQuality)}
                >
                  Devam Et <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Process Steps Section */}
        <section className="py-12 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4">
            <h3 className="text-xl font-bold text-center mb-8">Profesyonel Servis Süreci</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { step: '1', title: 'Cihaz Teslim Alındı', desc: 'Kargo veya elden teslim' },
                { step: '2', title: 'Ücretsiz Arıza Tespiti', desc: 'Detaylı teknik analiz' },
                { step: '3', title: 'Müşteri Onayı', desc: 'Fiyat ve süre bilgisi' },
                { step: '4', title: 'Onarım Tamamlandı', desc: 'Kalite kontrol yapıldı' },
                { step: '5', title: 'Teslim Edildi', desc: 'Kargo veya elden teslim' },
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-3 font-bold">
                    {item.step}
                  </div>
                  <h4 className="font-semibold text-sm mb-1">{item.title}</h4>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* Contact Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>İletişim Bilgileri</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Ad Soyad *</Label>
              <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="name-input" />
            </div>
            <div className="space-y-2">
              <Label>Telefon *</Label>
              <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="05XX XXX XX XX" required data-testid="phone-input" />
            </div>
            <div className="space-y-2">
              <Label>Şehir *</Label>
              <Select value={selectedCity} onValueChange={setSelectedCity}>
                <SelectTrigger data-testid="city-select"><SelectValue placeholder="Şehir seçin" /></SelectTrigger>
                <SelectContent>
                  {cities.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Randevu Tarihi (Opsiyonel)</Label>
              <Input type="date" value={formData.appointment_date} onChange={(e) => setFormData({...formData, appointment_date: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Kargo Takip No (Opsiyonel)</Label>
              <Input value={formData.cargo_number} onChange={(e) => setFormData({...formData, cargo_number: e.target.value})} />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="flex-1">İptal</Button>
              <Button type="submit" className="flex-1" disabled={isSubmitting} data-testid="submit-btn">
                {isSubmitting ? 'Gönderiliyor...' : 'Kayıt Oluştur'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="sm:max-w-md">
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold mb-2">Kayıt Oluşturuldu!</h2>
            <p className="text-muted-foreground mb-4">Servis kayıt kodunuz:</p>
            
            <div className="bg-muted rounded-lg p-4 mb-4">
              <div className="flex items-center justify-center gap-2">
                <span className="text-2xl font-mono font-bold" data-testid="service-code">{serviceCode}</span>
                <Button variant="ghost" size="icon" onClick={copyCode}>
                  <Copy className="w-5 h-5" />
                </Button>
              </div>
            </div>
            
            <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg mb-4">
              <strong>Önemli:</strong> Servis takip numaranız için bu kodu WhatsApp'tan danışmanlarımıza iletip alabilirsiniz.
            </p>
            
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowSuccessModal(false)} className="flex-1">
                <X className="w-4 h-4 mr-2" />Tamam
              </Button>
              <Button onClick={goToWhatsApp} className="flex-1 bg-green-600 hover:bg-green-700">
                <MessageCircle className="w-4 h-4 mr-2" />WhatsApp'a Git
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default ServicePage;
